/**
 * `next/head` — writes its children into document.head.
 *
 * Next hoisted these at render time on the server; here it is an effect, which means the
 * tags land a tick after first paint. That is fine for what is actually in them (title,
 * a meta description) and wrong for anything that must exist before the first paint —
 * which is why the referrer policy, the favicon and the activity-tracker script live in
 * public/index.html instead of being rendered through this.
 *
 * Only <title> and <meta> are handled; those are the two element types the ported code
 * puts in a <Head>. Anything else is left alone rather than half-supported.
 */

import { useEffect } from 'react';
import React from 'react';

function applyMeta(element: React.ReactElement): (() => void) | undefined {
  const props = element.props as Record<string, string>;

  if (element.type === 'title') {
    const previous = document.title;
    const children = (element.props as { children?: React.ReactNode }).children;
    document.title = String(children ?? '');
    return () => {
      document.title = previous;
    };
  }

  if (element.type === 'meta') {
    const key = props.name ? 'name' : props.property ? 'property' : null;
    if (!key) return undefined;

    const selector = `meta[${key}="${props[key]}"]`;
    const existing = document.head.querySelector<HTMLMetaElement>(selector);
    const tag = existing ?? document.createElement('meta');
    const previousContent = existing?.content;

    tag.setAttribute(key, props[key]);
    tag.content = props.content ?? '';
    if (!existing) document.head.appendChild(tag);

    return () => {
      // Restore rather than remove where the tag was already in the document — this
      // shim must not strip a policy tag that index.html set.
      if (existing && previousContent !== undefined) existing.content = previousContent;
      else tag.remove();
    };
  }

  return undefined;
}

export default function Head({ children }: { children?: React.ReactNode }) {
  useEffect(() => {
    const cleanups = React.Children.toArray(children)
      .filter(React.isValidElement)
      .map((child) => applyMeta(child as React.ReactElement))
      .filter(Boolean) as Array<() => void>;

    return () => cleanups.forEach((fn) => fn());
  }, [children]);

  return null;
}
